
import React, { useState } from 'react';
import type { DayOfWeek, Recipe, PlannedMeal, Role, MealType, Language } from '../types';
import { getTranslation } from '../utils/translations';
import { buildCookingSessionDocId } from '../utils/cookingSessionKeys';
import { isPlannedRecipeCookingDone, isPlannedRecipeCookingInProgress } from '../utils/cookingSessionSteps';
import { resolveMealSlotHeaderTitle } from '../utils/mealSlotTitle';
import type { CookingSessionStatus } from '../hooks/useCookingSessionStatuses';
import EditableMealTitle from './EditableMealTitle';
import PlusIcon from './icons/PlusIcon';
import ChevronDownIcon from './icons/ChevronDownIcon';
import ChevronUpIcon from './icons/ChevronUpIcon';
import CookingPotIcon from './icons/CookingPotIcon';
import ClockIcon from './icons/ClockIcon';

const CheckIcon = (props: React.SVGProps<SVGSVGElement>) => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={3} stroke="currentColor" {...props}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
    </svg>
);

interface DayCardProps {
    day: DayOfWeek;
    meals: (PlannedMeal & { recipe: Recipe })[];
    role: Role;
    canManage: boolean;
    onDropRecipe: (recipeId: string, mealType: MealType) => void;
    onRemoveRecipe: (plannedMealId: string) => void;
    onMoveMeal: (plannedMealId: string, fromDay: DayOfWeek, toDay: DayOfWeek, toType: MealType) => void;
    onUpdateMeal: (plannedMealId: string, updates: { servings?: number; status?: 'buy' | 'pantry' | 'prepared' }) => void;
    onRecipeClick: (recipe: Recipe) => void;
    onAddMeal: (type: MealType) => void;
    onGenerateMealShoppingList: (meals: (PlannedMeal & { recipe: Recipe })[]) => void;
    textSizeClass?: string;
    visibleMealTypes: MealType[];
    language?: Language;
    onMealDragStart: () => void;
    onMealDragEnd: () => void;
    onCookMeal: (meals: (PlannedMeal & { recipe: Recipe })[], type: MealType, initialRecipeId?: string) => void;
    defaultExpanded?: boolean;
    onEditMeal?: (meal: PlannedMeal & { recipe: Recipe }) => void;
    weekOffset?: number;
    cookingSessionStatuses?: Map<string, CookingSessionStatus>;
    slotMenuTitles?: Partial<Record<string, string>>;
    onUpdateMealSlotTitle?: (day: DayOfWeek, mealType: MealType, title: string | null) => void;
}

interface MealSlotProps {
    day: DayOfWeek;
    title: string;
    meals: (PlannedMeal & { recipe: Recipe })[];
    type: MealType;
    role: Role;
    canManage: boolean;
    onDropRecipe: (recipeId: string, mealType: MealType) => void;
    onRecipeClick: (recipe: Recipe) => void;
    onAddClick: () => void;
    language?: Language;
    onMealDragStart: () => void;
    onMealDragEnd: () => void;
    onCookMeal: (meals: (PlannedMeal & { recipe: Recipe })[], initialRecipeId?: string) => void;
    onEditMeal?: (meal: PlannedMeal & { recipe: Recipe }) => void;
    weekOffset?: number;
    cookingSessionStatuses?: Map<string, CookingSessionStatus>;
    onUpdateMealSlotTitle?: (day: DayOfWeek, mealType: MealType, title: string | null) => void;
}

const CompactMealRow: React.FC<{
    meal: PlannedMeal & { recipe: Recipe };
    canManage: boolean;
    onEdit: () => void;
    onResumeCooking?: () => void;
    language?: Language;
    isRecipeDone?: boolean;
    isRecipeInProgress?: boolean;
}> = ({ meal, canManage, onEdit, onResumeCooking, language, isRecipeDone = false, isRecipeInProgress = false }) => {
    const t = (key: Parameters<typeof getTranslation>[1]) => getTranslation(language, key);
    const isMeat = meal.recipe.ingredients.some(i => i.isMeat);
    const accentClass = isMeat ? "bg-rose-500" : "bg-sky-400";
    
    const sourcingStatus = meal.sourcingStatus || 'buy';
    const statusIcon = sourcingStatus === 'pantry' ? '🏠' : sourcingStatus === 'prepared' ? '🧊' : null;

    return (
        <div 
            className={`
                group flex items-center justify-between w-full py-1 min-h-[28px] relative hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-all overflow-hidden cursor-pointer
                ${meal.recipe.isDeleted ? 'opacity-50' : ''}
                ${isRecipeDone ? 'bg-brand-sage/5 dark:bg-brand-sage/10' : isRecipeInProgress ? 'bg-amber-50/80 dark:bg-amber-950/20' : sourcingStatus !== 'buy' ? 'bg-slate-50/50 dark:bg-slate-800/30' : ''}
            `}
            onClick={(e) => { e.stopPropagation(); onEdit(); }}
        >
            <div className={`absolute top-0 bottom-0 w-1 rtl:right-0 ltr:left-0 ${isRecipeDone ? 'bg-brand-sage' : isRecipeInProgress ? 'bg-amber-400' : accentClass}`} />
            
            <div className="flex items-center gap-2 min-w-0 flex-grow ltr:pl-3 rtl:pr-3">
                <span className={`text-sm truncate ${isRecipeDone ? 'text-brand-sage dark:text-brand-sage font-semibold' : 'text-slate-700 dark:text-slate-200'} ${meal.recipe.isDeleted ? 'line-through text-slate-400' : ''}`}>
                    {meal.recipe.name || meal.recipeName}
                </span>
                {isRecipeDone && (
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-brand-sage/10 text-brand-sage border border-brand-sage/20 shrink-0">
                        <CheckIcon className="w-3 h-3" />
                        {t('recipeReadyBadge')}
                    </span>
                )}
                {!isRecipeDone && isRecipeInProgress && onResumeCooking && (
                    <button
                        type="button"
                        onClick={(e) => {
                            e.stopPropagation();
                            onResumeCooking();
                        }}
                        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-amber-100 text-amber-700 border border-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-700/40 shrink-0 hover:bg-amber-200/80 dark:hover:bg-amber-900/50 transition-colors"
                        title={t('recipeInProgressBadge')}
                    >
                        <ClockIcon className="w-3 h-3" />
                        {t('recipeInProgressBadge')}
                    </button>
                )}
                {!isRecipeDone && !isRecipeInProgress && statusIcon && (
                    <span className="text-[10px]" title={sourcingStatus}>{statusIcon}</span>
                )}
            </div>

            <div className="flex items-center gap-2 shrink-0 ltr:pr-2 rtl:pl-2">
                <span className="text-[10px] font-mono text-gray-500 dark:text-slate-400 bg-gray-50 dark:bg-slate-800 px-1.5 rounded-sm">
                    {meal.plannedServings}
                </span>
            </div>
        </div>
    );
};

const MealSlot: React.FC<MealSlotProps> = ({ 
    day, title, meals, type, canManage, onDropRecipe,
    onRecipeClick, onAddClick, language, onMealDragStart, onMealDragEnd, onCookMeal, onEditMeal,
    weekOffset = 0, cookingSessionStatuses, onUpdateMealSlotTitle,
}) => {
    const [isOver, setIsOver] = useState(false);
    const t = (key: Parameters<typeof getTranslation>[1]) => getTranslation(language, key);
    const prepTabLabel = t('prepTab');
    const sessionKey = buildCookingSessionDocId(weekOffset, day, type);
    const sessionStatus = cookingSessionStatuses?.get(sessionKey);

    const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
        if (!canManage) return;
        e.preventDefault();
        setIsOver(true);
    };

    const handleDragLeave = () => {
        if (!canManage) return;
        setIsOver(false);
    };

    const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
        if (!canManage) return;
        e.preventDefault();
        setIsOver(false);
        
        const recipeId = e.dataTransfer.getData("application/recipe-id");
        if (recipeId) onDropRecipe(recipeId, type);
    };

    const getIcon = () => {
        switch (type) {
            case 'Breakfast': return '🌅';
            case 'Lunch': return '☀️';
            case 'Dinner': return '🌙';
            default: return '🍽️';
        }
    };

    const getHeaderStyle = () => {
        switch (type) {
            case 'Breakfast': return 'bg-amber-50 dark:bg-amber-900/20 text-amber-900 dark:text-amber-100';
            case 'Lunch': return 'bg-orange-50 dark:bg-orange-900/20 text-orange-900 dark:text-orange-100';
            case 'Dinner': return 'bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200';
            default: return 'bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-200';
        }
    };

    return (
        <div
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            className={`flex flex-col mt-2 rounded-lg transition-all duration-200 border border-transparent ${isOver ? 'bg-sky-50/50 dark:bg-sky-900/20 border-sky-100 dark:border-sky-800' : ''}`}
        >
            <div className={`flex flex-row items-center justify-between py-1.5 px-3 rounded-t-lg ${getHeaderStyle()}`}>
                <div className="flex items-center gap-2">
                    <span className="text-sm leading-none" role="img" aria-hidden="true">{getIcon()}</span>
                    <h4 className="text-xs font-bold uppercase tracking-wide opacity-90 min-w-0">
                        <EditableMealTitle
                            value={title}
                            canEdit={canManage && Boolean(onUpdateMealSlotTitle)}
                            onSave={(nextTitle) => onUpdateMealSlotTitle?.(day, type, nextTitle)}
                            placeholder={t('mealNamePlaceholder')}
                            editLabel={t('editMealName')}
                            inputClassName="text-xs font-bold uppercase tracking-wide bg-white/80 dark:bg-slate-900/80 border border-current/20 rounded px-1.5 py-0.5 min-w-[6rem] max-w-[12rem]"
                        />
                    </h4>
                    {sessionStatus?.mealCompleted && meals.length > 0 && (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-brand-sage/15 text-brand-sage border border-brand-sage/20">
                            <CheckIcon className="w-3 h-3" />
                            {t('mealCompletedTitle')}
                        </span>
                    )}
                </div>
                <div className="flex items-center gap-1">
                    {meals.length > 0 && (
                        <button 
                            id={`planner-cook-btn-${day}-${type}`}
                            onClick={() => onCookMeal(meals)}
                            className="text-current opacity-60 hover:opacity-100 transition-colors p-1 rounded-full w-5 h-5 flex items-center justify-center"
                            title={t('cookMeal')}
                        >
                            <CookingPotIcon className="w-3.5 h-3.5" />
                        </button>
                    )}
                    {canManage && (
                        <button 
                            id={`planner-add-btn-${day}-${type}`}
                            onClick={onAddClick}
                            className="w-5 h-5 rounded-full hover:bg-black/5 flex items-center justify-center text-current opacity-50 hover:opacity-100 transition-all"
                        >
                            <PlusIcon className="w-3.5 h-3.5" />
                        </button>
                    )}
                </div>
            </div>
            
            <div className="flex flex-col gap-1 mt-1">
                {meals.map((meal) => {
                    const isRecipeDone = isPlannedRecipeCookingDone(
                        meal.recipe.id,
                        meal.recipe,
                        sessionStatus,
                        prepTabLabel
                    );
                    const isRecipeInProgress = !isRecipeDone && isPlannedRecipeCookingInProgress(
                        meal.recipe.id,
                        meal.recipe,
                        sessionStatus,
                        prepTabLabel
                    );

                    return (
                        <CompactMealRow
                            key={meal.id || meal.recipeId}
                            meal={meal}
                            canManage={canManage}
                            language={language}
                            isRecipeDone={isRecipeDone}
                            isRecipeInProgress={isRecipeInProgress}
                            onResumeCooking={isRecipeInProgress ? () => onCookMeal(meals, meal.recipe.id) : undefined}
                            onEdit={() => onEditMeal && onEditMeal(meal)}
                        />
                    );
                })}
            </div>
        </div>
    );
};

const DayCard: React.FC<DayCardProps> = ({ 
    day, meals, role, canManage, onDropRecipe, onRemoveRecipe, onMoveMeal, onUpdateMeal,
    onRecipeClick, onAddMeal, visibleMealTypes, language, 
    onMealDragStart, onMealDragEnd, onCookMeal, defaultExpanded = true, onEditMeal,
    weekOffset = 0, cookingSessionStatuses, slotMenuTitles, onUpdateMealSlotTitle,
}) => {
    const [isRowExpanded, setIsRowExpanded] = useState(defaultExpanded);
    const t = (key: any) => getTranslation(language, key);
    const titlePlan = { slotMenuTitles };

    return (
        <div className="flex flex-col bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-slate-200 dark:border-slate-800 mb-4 overflow-hidden transition-all duration-300">
            <div 
                className="w-full flex justify-between items-center p-4 bg-slate-50 dark:bg-slate-800 border-b border-gray-100 dark:border-slate-700 cursor-pointer hover:bg-slate-100/80 dark:hover:bg-slate-700 transition-all"
                onClick={() => setIsRowExpanded(!isRowExpanded)}
            >
                <div className="flex items-center gap-4">
                    <h3 className="font-bold text-slate-800 dark:text-slate-100 text-xl">
                        {t('day_' + day)}
                    </h3>
                </div>
                <div className="text-slate-300 dark:text-slate-600">
                    {isRowExpanded ? <ChevronUpIcon className="w-6 h-6" /> : <ChevronDownIcon className="w-6 h-6" />}
                </div>
            </div>

            {isRowExpanded && (
                <div className="flex flex-col pb-4 px-1">
                    {visibleMealTypes.map((type) => (
                        <MealSlot 
                            key={type}
                            day={day}
                            title={resolveMealSlotHeaderTitle(titlePlan, day, type, t)} 
                            type={type}
                            meals={meals.filter(m => m.mealType === type || (type === 'Dinner' && !m.mealType))}
                            role={role}
                            canManage={canManage}
                            onDropRecipe={onDropRecipe}
                            onRecipeClick={onRecipeClick}
                            onAddClick={() => onAddMeal(type)}
                            language={language}
                            onMealDragStart={onMealDragStart}
                            onMealDragEnd={onMealDragEnd}
                            onCookMeal={(ms, initialRecipeId) => onCookMeal(ms, type, initialRecipeId)}
                            onEditMeal={onEditMeal}
                            weekOffset={weekOffset}
                            cookingSessionStatuses={cookingSessionStatuses}
                            onUpdateMealSlotTitle={onUpdateMealSlotTitle}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

export default DayCard;
